import React, { useEffect, useState } from 'react';
import { useParams, Link, useLocation, Navigate } from 'react-router-dom';
import { getCompany, getPages } from '../../services/companyService.js';
import { HeroLayout, GridLayout, TextLayout, TextImageLayout, GalleryLayout } from './Templates/index.js';
import { collection, query, where, getDocs } from 'firebase/firestore';
import { db } from '../../services/firebase.js';
import { PageSkeleton } from '../Common/SkeletonLoader.js';

// Add this ordered days array at the top of HomePage.js
const orderedDays = [
    'Mandag',
    'Tirsdag',
    'Onsdag',
    'Torsdag',
    'Fredag',
    'Lørdag',
    'Søndag'
];

const HomePage = ({ subdomain }) => {
    const { cvr, slug } = useParams();
    const location = useLocation(); // Add this
    const [company, setCompany] = useState(null);
    const [currentPage, setCurrentPage] = useState(null);  // Add setCurrentPage
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const [pages, setPages] = useState([]);

    // Replace firma.håndværkernet.dk with subdomain
    const adminBaseUrl = window.location.hostname === 'localhost' 
        ? 'http://localhost:3000'
        : `https://${subdomain}.xn--hndvrkernet-x8aq.dk`;

    // Check for the newSite parameter in the URL
    const [showWelcomePopup, setShowWelcomePopup] = useState(() => {
        const params = new URLSearchParams(location.search);
        const isNewSite = params.get('newsite') === 'true';
        
        if (!isNewSite) return false;

        const storageKey = subdomain ? `welcomeSeen_${subdomain}` : `welcomeSeen_${cvr}`;
        const hasSeenWelcome = localStorage.getItem(storageKey);
        return !hasSeenWelcome;
    });

    const theme = company?.theme || 'amber'; // Hvis intet tema er valgt, brug amber

    // Egen helper (eksempel)
    function getThemeClasses(themeKey) {
        const themes = {
            amber: {
                primary: 'bg-amber-800',
                secondary: 'bg-amber-600',
                light: 'bg-amber-50',
                hover: 'hover:bg-amber-600',
                text: 'text-amber-800',
                lightText: 'text-amber-200',
                hoverText: 'hover:text-amber-600',
                lightHover: 'hover:text-amber-50',
                gradient: 'from-amber-800 to-amber-900',
                overlay: 'from-amber-900/75 to-amber-800/75'
            },
            blue: {
                primary: 'bg-blue-800',
                secondary: 'bg-blue-600',
                light: 'bg-blue-50',
                hover: 'hover:bg-blue-600',
                text: 'text-blue-800',
                lightText: 'text-blue-200',
                hoverText: 'hover:text-blue-600',
                lightHover: 'hover:text-blue-50',
                gradient: 'from-blue-800 to-blue-900',
                overlay: 'from-blue-900/75 to-blue-800/75'
            },
            red: {
                primary: 'bg-red-800',
                secondary: 'bg-red-600',
                light: 'bg-red-50',
                hover: 'hover:bg-red-600',
                text: 'text-red-800',
                lightText: 'text-red-200',
                hoverText: 'hover:text-red-600',
                lightHover: 'hover:text-red-50',
                gradient: 'from-red-800 to-red-900',
                overlay: 'from-red-900/75 to-red-800/75'
            },
            green: {
                primary: 'bg-green-800',
                secondary: 'bg-green-600',
                light: 'bg-green-50',
                hover: 'hover:bg-green-600',
                text: 'text-green-800',
                lightText: 'text-green-200',
                hoverText: 'hover:text-green-600',
                lightHover: 'hover:text-green-50',
                gradient: 'from-green-800 to-green-900',
                overlay: 'from-green-900/75 to-green-800/75'
            },
            purple: {
                primary: 'bg-purple-800',
                secondary: 'bg-purple-600',
                light: 'bg-purple-50',
                hover: 'hover:bg-purple-600',
                text: 'text-purple-800',
                lightText: 'text-purple-200',
                hoverText: 'hover:text-purple-600',
                lightHover: 'hover:text-purple-50',
                gradient: 'from-purple-800 to-purple-900',
                overlay: 'from-purple-900/75 to-purple-800/75'
            },
            indigo: {
                primary: 'bg-indigo-800',
                secondary: 'bg-indigo-600',
                light: 'bg-indigo-50',
                hover: 'hover:bg-indigo-600',
                text: 'text-indigo-800',
                lightText: 'text-indigo-200',
                hoverText: 'hover:text-indigo-600',
                lightHover: 'hover:text-indigo-50',
                gradient: 'from-indigo-800 to-indigo-900',
                overlay: 'from-indigo-900/75 to-indigo-800/75'
            },
            teal: {
                primary: 'bg-teal-800',
                secondary: 'bg-teal-600',
                light: 'bg-teal-50',
                hover: 'hover:bg-teal-600',
                text: 'text-teal-800',
                lightText: 'text-teal-200',
                hoverText: 'hover:text-teal-600',
                lightHover: 'hover:text-teal-50',
                gradient: 'from-teal-800 to-teal-900',
                overlay: 'from-teal-900/75 to-teal-800/75'
            },
            emerald: {
                primary: 'bg-emerald-800',
                secondary: 'bg-emerald-600',
                light: 'bg-emerald-50',
                hover: 'hover:bg-emerald-600',
                text: 'text-emerald-800',
                lightText: 'text-emerald-200',
                hoverText: 'hover:text-emerald-600',
                lightHover: 'hover:text-emerald-50',
                gradient: 'from-emerald-800 to-emerald-900',
                overlay: 'from-emerald-900/75 to-emerald-800/75'
            },
            rose: {
                primary: 'bg-rose-800',
                secondary: 'bg-rose-600',
                light: 'bg-rose-50',
                hover: 'hover:bg-rose-600',
                text: 'text-rose-800',
                lightText: 'text-rose-200',
                hoverText: 'hover:text-rose-600',
                lightHover: 'hover:text-rose-50',
                gradient: 'from-rose-800 to-rose-900',
                overlay: 'from-rose-900/75 to-rose-800/75'
            },
            cyan: {
                primary: 'bg-cyan-800',
                secondary: 'bg-cyan-600',
                light: 'bg-cyan-50',
                hover: 'hover:bg-cyan-600',
                text: 'text-cyan-800',
                lightText: 'text-cyan-200',
                hoverText: 'hover:text-cyan-600',
                lightHover: 'hover:text-cyan-50',
                gradient: 'from-cyan-800 to-cyan-900',
                overlay: 'from-cyan-900/75 to-cyan-800/75'
            },
            sky: {
                primary: 'bg-sky-800',
                secondary: 'bg-sky-600',
                light: 'bg-sky-50',
                hover: 'hover:bg-sky-600',
                text: 'text-sky-800',
                lightText: 'text-sky-200',
                hoverText: 'hover:text-sky-600',
                lightHover: 'hover:text-sky-50',
                gradient: 'from-sky-800 to-sky-900',
                overlay: 'from-sky-900/75 to-sky-800/75'
            },
            orange: {
                primary: 'bg-orange-800',
                secondary: 'bg-orange-600',
                light: 'bg-orange-50',
                hover: 'hover:bg-orange-600',
                text: 'text-orange-800',
                lightText: 'text-orange-200',
                hoverText: 'hover:text-orange-600',
                lightHover: 'hover:text-orange-50',
                gradient: 'from-orange-800 to-orange-900',
                overlay: 'from-orange-900/75 to-orange-800/75'
            },
            slate: {
                primary: 'bg-slate-800',
                secondary: 'bg-slate-600',
                light: 'bg-slate-50',
                hover: 'hover:bg-slate-600',
                text: 'text-slate-800',
                lightText: 'text-slate-200',
                hoverText: 'hover:text-slate-600',
                lightHover: 'hover:text-slate-50',
                gradient: 'from-slate-800 to-slate-900',
                overlay: 'from-slate-900/75 to-slate-800/75'
            },
            zinc: {
                primary: 'bg-zinc-800',
                secondary: 'bg-zinc-600',
                light: 'bg-zinc-50',
                hover: 'hover:bg-zinc-600',
                text: 'text-zinc-800',
                lightText: 'text-zinc-200',
                hoverText: 'hover:text-zinc-600',
                lightHover: 'hover:text-zinc-50',
                gradient: 'from-zinc-800 to-zinc-900',
                overlay: 'from-zinc-900/75 to-zinc-800/75'
            },
            neutral: {
                primary: 'bg-neutral-800',
                secondary: 'bg-neutral-600',
                light: 'bg-neutral-50',
                hover: 'hover:bg-neutral-600',
                text: 'text-neutral-800',
                lightText: 'text-neutral-200',
                hoverText: 'hover:text-neutral-600',
                lightHover: 'hover:text-neutral-50',
                gradient: 'from-neutral-800 to-neutral-900',
                overlay: 'from-neutral-900/75 to-neutral-800/75'
            },
            lime: {
                primary: 'bg-lime-800',
                secondary: 'bg-lime-600',
                light: 'bg-lime-50',
                hover: 'hover:bg-lime-600',
                text: 'text-lime-800',
                lightText: 'text-lime-200',
                hoverText: 'hover:text-lime-600',
                lightHover: 'hover:text-lime-50',
                gradient: 'from-lime-800 to-lime-900',
                overlay: 'from-lime-900/75 to-lime-800/75'
            },
            yellow: {
                primary: 'bg-yellow-800',
                secondary: 'bg-yellow-600',
                light: 'bg-yellow-50',
                hover: 'hover:bg-yellow-600',
                text: 'text-yellow-800',
                lightText: 'text-yellow-200',
                hoverText: 'hover:text-yellow-600',
                lightHover: 'hover:text-yellow-50',
                gradient: 'from-yellow-800 to-yellow-900',
                overlay: 'from-yellow-900/75 to-yellow-800/75'
            },
            stone: {
                primary: 'bg-stone-800',
                secondary: 'bg-stone-600',
                light: 'bg-stone-50',
                hover: 'hover:bg-stone-600',
                text: 'text-stone-800',
                lightText: 'text-stone-200',
                hoverText: 'hover:text-stone-600',
                lightHover: 'hover:text-stone-50',
                gradient: 'from-stone-800 to-stone-900',
                overlay: 'from-stone-900/75 to-stone-800/75'
            },
            fuchsia: {
                primary: 'bg-fuchsia-800',
                secondary: 'bg-fuchsia-600',
                light: 'bg-fuchsia-50',
                hover: 'hover:bg-fuchsia-600',
                text: 'text-fuchsia-800',
                lightText: 'text-fuchsia-200',
                hoverText: 'hover:text-fuchsia-600',
                lightHover: 'hover:text-fuchsia-50',
                gradient: 'from-fuchsia-800 to-fuchsia-900',
                overlay: 'from-fuchsia-900/75 to-fuchsia-800/75'
            },
            pink: {
                primary: 'bg-pink-800',
                secondary: 'bg-pink-600',
                light: 'bg-pink-50',
                hover: 'hover:bg-pink-600',
                text: 'text-pink-800',
                lightText: 'text-pink-200',
                hoverText: 'hover:text-pink-600',
                lightHover: 'hover:text-pink-50',
                gradient: 'from-pink-800 to-pink-900',
                overlay: 'from-pink-900/75 to-pink-800/75'
            }
        };

        return themes[themeKey] || themes.amber;
    }

    const themeClasses = getThemeClasses(theme);

    useEffect(() => {
        const fetchData = async () => {
            try {
                let companyData;
                const hostname = window.location.hostname;
                const isLocalhost = hostname === 'localhost' || hostname === '127.0.0.1';
                
                // Handle subdomain lookup
                if (subdomain || (!isLocalhost && hostname !== 'håndværkernet.dk')) {
                    const actualSubdomain = subdomain || hostname.split('.')[0];
                    const companiesRef = collection(db, 'companies');
                    const q = query(companiesRef, where('domain.subdomain', '==', actualSubdomain));
                    const querySnapshot = await getDocs(q);
                    
                    if (querySnapshot.empty) {
                        throw new Error('NOT_FOUND');
                    }
                    companyData = {
                        id: querySnapshot.docs[0].id,
                        ...querySnapshot.docs[0].data()
                    };
                } else {
                    try {
                        companyData = await getCompany(cvr);
                    } catch (err) {
                        throw new Error('NOT_FOUND');
                    }
                }

                setCompany(companyData);
                const pagesData = await getPages(companyData.cvr);
                setPages(pagesData);
                
                // Find page by slug or default to homepage
                const targetPage = slug 
                    ? pagesData.find(page => page.slug === slug)
                    : pagesData.find(page => page.type === 'homepage');

                if (targetPage) {
                    setCurrentPage(targetPage);
                }
            } catch (error) {
                if (error.message === 'NOT_FOUND') {
                    // Redirect to 404 page
                    window.location.href = '/404';
                } else {
                    setError(error.message);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [cvr, subdomain, slug]); // Add slug to dependencies

    useEffect(() => {
        if (currentPage) {
            // Update page metadata
            document.title = currentPage.metaTitle || `${company.name} - Forside`;
            
            // Update meta description
            let metaDescription = document.querySelector('meta[name="description"]');
            if (!metaDescription) {
                metaDescription = document.createElement('meta');
                metaDescription.name = 'description';
                document.head.appendChild(metaDescription);
            }
            metaDescription.content = currentPage.metaDescription || 
                `${company.name} er en professionel håndværkervirksomhed med fokus på kvalitet og kundetilfredshed`;
        }
    }, [currentPage, company]);

    if (loading) return <PageSkeleton type="website" />;
    if (error) return <Navigate to="/404" replace />;
    if (!company) return null;

    const renderLayout = (page) => {
        switch (page.layout) {
            case 'grid':
                return <GridLayout content={page.content} subdomain={subdomain} />;
            case 'text':
                return <TextLayout content={page.content} subdomain={subdomain} />;
            case 'text-image':
                return <TextImageLayout content={page.content} subdomain={subdomain} />;
            case 'gallery':
                return <GalleryLayout content={page.content} />;
            case 'hero':
            default:
                return (
                    <HeroLayout 
                        content={page.content} 
                        themeClasses={themeClasses}
                        subdomain={subdomain}
                    />
                );
        }
    };

    const renderContent = () => {
        if (!currentPage) return null;

        switch (currentPage.layout) {
            case 'text':
            case 'text-image':
            case 'gallery':
                // These layouts don't include hero
                return (
                    <main className="py-20 bg-white">
                        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                            {renderLayout(currentPage)}
                        </div>
                    </main>
                );
            case 'hero':
            default:
                // Hero layout includes its own hero section
                return renderLayout(currentPage);
        }
    };

    // Helper function for date comparison
    const getDateValue = (date) => {
        if (!date) return 0;
        if (date instanceof Date) return date.getTime();
        if (typeof date === 'string') return new Date(date).getTime();
        if (typeof date.toDate === 'function') return date.toDate().getTime();
        return 0;
    };

    const handleCloseWelcome = () => {
        setShowWelcomePopup(false);
        // Use the same key format as above
        const storageKey = subdomain ? `welcomeSeen_${subdomain}` : `welcomeSeen_${cvr}`;
        localStorage.setItem(storageKey, 'true');
    };

    return (
        <div className="min-h-screen flex flex-col bg-white font-roboto">
            {/* Welcome popup for new admins */}
            {showWelcomePopup && (
                <div className="fixed inset-0 z-[100]">
                    <div className="absolute inset-0 bg-gray-900/60 backdrop-blur-sm" />
                    <div className="relative h-full flex items-center justify-center p-4">
                        <div
                        className="bg-white rounded-2xl shadow-2xl max-w-xs sm:max-w-lg md:max-w-2xl w-full relative overflow-hidden p-2 sm:p-8"
                        style={{ maxHeight: '90vh', overflowY: 'auto' }}
                        >
                            {/* Opdateret gradient med brand farver */}
                            <div className="absolute top-0 left-0 right-0 h-2 bg-gradient-to-r from-brand-dark to-brand-dark/80" />
                            
                            <div className="p-8">
                                <div className="flex justify-between items-start">
                                    <div>
                                        <h2 className="text-lg sm:text-3xl font-bold font-montserrat text-gray-900 mb-2 break-words">
                                            Tillykke med din nye hjemmeside! 🎉
                                        </h2>
                                        <p className="text-gray-500">Lad os komme i gang med at gøre den personlig.</p>
                                        <p className="mt-1 text-green-800 text-xs">Der er sendt en velkomstmail til den email adresse du indtastede tidligere.<br />Hvis du ikke kan se emailen, så kan det være den er landet i din spam mappe.</p>
                                    </div>
                                    <button 
                                        onClick={() => setShowWelcomePopup(false)}
                                        className="text-gray-400 hest hover:text-gray-500 transition-colors p-1 hover:bg-gray-100 rounded-full"
                                    >
                                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                        </svg>
                                    </button>
                                </div>

                                <div className="mt-6 space-y-6">
                                    <div className="mt-6 space-y-6">
                                        <p className="text-gray-500">Nu kan du enten vælge at se din nye hjemmeside, eller gå direkte til kontrolpanelet hvor du kan:</p>
                                        <div className="flex items-start">
                                            <div className="flex-shrink-0 bg-brand-light/20 rounded-lg p-3">
                                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6V4m0 2a2 2 0 100 4m0-4a2 2 0 110 4m-6 8a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4m6 6v10m6-2a2 2 0 100-4m0 4a2 2 0 110-4m0 4v2m0-6V4" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <h3 className="text-lg font-medium font-montserrat text-gray-900">Tilpas design</h3>
                                                <p className="mt-1 text-gray-500">
                                                    Vælg mellem forskellige farvetemaer og designindstillinger for at give din hjemmeside et personligt udtryk der matcher din virksomhed.
                                                </p>
                                            </div>
                                        </div>
                                        
                                        <div className="flex items-start">
                                            <div className="flex-shrink-0 bg-brand-light/20 rounded-lg p-3">
                                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <h3 className="text-lg font-medium font-montserrat text-gray-900">Redigere indhold</h3>
                                                <p className="mt-1 text-gray-500">
                                                    Her kan du oprette sider, redigere tekster, billeder og meget mere.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start">
                                            <div className="flex-shrink-0 bg-brand-light/20 rounded-lg p-3">
                                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0  012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <h3 className="text-lg font-medium font-montserrat text-gray-900">Uploade billeder</h3>
                                                <p className="mt-1 text-gray-500">
                                                    I kontrolpanelet finder du "Billedearkiv", hvor du kan uploade og 
                                                    administrere dine billeder.
                                                </p>
                                            </div>
                                        </div>

                                        <div className="flex items-start">
                                            <div className="flex-shrink-0 bg-brand-light/20 rounded-lg p-3">
                                                <svg className="w-6 h-6 text-brand-dark" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M8.228 9c.549-1.165 2.03-2 3.772-2 2.21 0 4 1.343 4 3 0 1.4-1.278 2.575-3.006 2.907-.542.104-.994.54-.994 1.093m0 3h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                                                </svg>
                                            </div>
                                            <div className="ml-4">
                                                <h3 className="text-lg font-medium font-montserrat text-gray-900">Brug for hjælp?</h3>
                                                <p className="mt-1 text-gray-500">
                                                    Kontakt os på <a href="mailto:kontakt@haandvaerkernet.dk" className="text-brand-dark hover:underline">kontakt@haandvaerkernet.dk</a> hvis du har spørgsmål eller brug for hjælp.
                                                </p>
                                            </div>
                                        </div>
                                    </div>
                                </div>

                                <div className="mt-8">
                                    <div className="flex flex-col items-end">
                                        <div className="flex space-x-4">
                                            <a
                                                href={`${adminBaseUrl}/admin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base bg-white border-2 border-brand-dark text-brand-dark rounded-lg hover:bg-brand-light/20 transform hover:-translate-y-0.5 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                                            >
                                                Gå til dit kontrolpanel
                                            </a>
                                            <button
                                                onClick={handleCloseWelcome}
                                                className="px-4 py-2 text-sm sm:px-6 sm:py-3 sm:text-base bg-gradient-to-br from-brand-dark to-brand-dark/90 text-white rounded-lg hover:from-brand-dark/90 hover:to-brand-dark transform hover:-translate-y-0.5 transition-all duration-200 shadow-lg hover:shadow-xl font-medium"
                                            >
                                                Se hjemmeside
                                            </button>
                                        </div>
                                        <p className="mt-3 text-sm text-gray-500">
                                            Du kan altid tilgå kontrolpanelet på{' '}
                                            <a 
                                                href={`${adminBaseUrl}/admin`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className="text-brand-dark hover:text-brand-dark/90 hover:underline"
                                            >
                                                {`https://${subdomain}.håndværkernet.dk/admin`}
                                            </a>
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Modern Sticky Header */}
            <header className="bg-white text-black shadow-sm z-50">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
                    <div className="flex justify-between items-center">
                        <div className="flex items-center">
                            <a href={subdomain ? "/" : `/${company.cvr}`} className="flex items-center">
                                {company.logo ? (
                                    <img 
                                        src={company.logo} 
                                        alt={`${company.name} logo`}
                                        className="h-20 w-auto"
                                    />
                                ) : (
                                    <h1 className="text-2xl font-bold font-montserrat text-gray-900">{company.name}</h1>
                                )}
                            </a>
                        </div>

                        {/* Mobile menu button */}
                        <button 
                            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                            className="md:hidden inline-flex items-center justify-center p-2 rounded-md text-gray-600 hover:text-amber-800 hover:bg-gray-100"
                        >
                            <span className="sr-only">Åbn menu</span>
                            <svg 
                                className={`h-6 w-6 ${isMobileMenuOpen ? 'hidden' : 'block'}`}
                                stroke="currentColor" 
                                fill="none" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                            </svg>
                            <svg 
                                className={`h-6 w-6 ${isMobileMenuOpen ? 'block' : 'hidden'}`}
                                stroke="currentColor" 
                                fill="none" 
                                viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Desktop navigation */}
                        <nav className="hidden md:flex items-center space-x-1 font-montserrat">
                            {pages
                                .sort((a, b) => getDateValue(a.createdAt) - getDateValue(b.createdAt))
                                .map(page => (
                                    page.type !== 'homepage' && (
                                        <Link 
                                            key={page.id}
                                            to={subdomain ? `/${page.slug}` : `/${cvr}/${page.slug}`}
                                            className={`px-4 py-2 rounded-md text-base font-medium relative group transition-all duration-200
                                                ${currentPage?.id === page.id 
                                                    ? `${themeClasses.text} ${themeClasses.light}` 
                                                    : `${themeClasses.text}`
                                                }`}
                                        >
                                            <span className="relative z-10">{page.pageName || page.title}</span>
                                            <span className={`${themeClasses.primary} absolute bottom-0 left-0 w-full h-0.5 transform scale-x-0 group-hover:scale-x-100 transition-transform duration-200`} />
                                        </Link>
                                    )
                                ))}
                            <a 
                                href="#kontakt" 
                                className={`ml-4 ${themeClasses.primary} ${themeClasses.hover} text-white px-6 py-2 rounded-md transform hover:-translate-y-0.5 transition-all duration-200 shadow-md hover:shadow-lg font-medium`}
                            >
                                Kontakt
                            </a>
                        </nav>
                    </div>

                    {/* Mobile menu */}
                    <div className={`${isMobileMenuOpen ? 'block' : 'hidden'} md:hidden`}>
                        <div className="p-4 space-y-2">
                            {pages
                                .sort((a, b) => getDateValue(a.createdAt) - getDateValue(b.createdAt))
                                .map(page => (
                                    page.type !== 'homepage' && (
                                        <Link 
                                            key={page.id}
                                            to={subdomain ? `/${page.slug}` : `/${cvr}/${page.slug}`}
                                            className={`block px-4 py-3 rounded-md text-gray-600 font-medium transition-all duration-200
                                                ${currentPage?.id === page.id 
                                                    ? 'text-amber-800 bg-amber-50' 
                                                    : 'hover:text-amber-800 hover:bg-gray-50'
                                                }`}
                                            onClick={() => setIsMobileMenuOpen(false)}
                                        >
                                            {page.pageName || page.title}
                                        </Link>
                                    )
                                ))}
                        </div>
                    </div>
                </div>
            </header>

            <div className="bg-gray-50 font-roboto">
                {renderContent()}
            </div>

            {/* Contact Section */}
            <section id="kontakt" className="bg-gray-50 py-20 font-roboto">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid md:grid-cols-2 gap-12">
                        <div>
                            <h2 className="text-3xl font-bold font-montserrat text-gray-900 mb-6">Kontakt os</h2>
                            <div className="space-y-4">
                                <p className="flex items-center text-gray-600">
                                    <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                                    </svg>
                                    {company.address}, {company.zipcode} {company.city}
                                </p>
                                {company.phone && (
                                    <p className="flex items-center text-gray-600">
                                        <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                                        </svg>
                                        <a href={`tel:${company.phone}`} className="hover:text-blue-600">
                                            {company.phone}
                                        </a>
                                    </p>
                                )}
                                {company.email && (
                                    <p className="flex items-center text-gray-600">
                                        <svg className="w-6 h-6 mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                                        </svg>
                                        <a href={`mailto:${company.email}`} className="hover:text-blue-600">
                                            {company.email}
                                        </a>
                                    </p>
                                )}
                            </div>
                        </div>
                        {company.hours?.showHours === true && (
                            <div className="bg-white rounded-lg shadow-lg p-6">
                                <h3 className="text-xl font-semibold font-montserrat mb-4">Telefonen er åben</h3>
                                {company.hours ? (
                                    orderedDays.map((day) => (
                                        <div key={day} className="flex justify-between py-2 border-b border-gray-200 last:border-0">
                                            <span className="font-medium">{day}</span>
                                            <span>{company.hours[day] || 'Lukket'}</span>
                                        </div>
                                    ))
                                ) : (
                                    // Default opening hours
                                    <>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Mandag</span>
                                            <span>07:00 - 16:00</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Tirsdag</span>
                                            <span>07:00 - 16:00</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Onsdag</span>
                                            <span>07:00 - 16:00</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Torsdag</span>
                                            <span>07:00 - 16:00</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Fredag</span>
                                            <span>07:00 - 15:00</span>
                                        </div>
                                        <div className="flex justify-between py-2 border-b border-gray-200">
                                            <span className="font-medium">Lørdag</span>
                                            <span>Lukket</span>
                                        </div>
                                        <div className="flex justify-between py-2">
                                            <span className="font-medium">Søndag</span>
                                            <span>Lukket</span>
                                        </div>
                                    </>
                                )}
                            </div>
                        )}
                    </div>
                </div>
            </section>

            {/* Footer */}
            <footer className={`${themeClasses.primary} text-white font-montserrat`}>
                <div className="max-w-7xl mx-auto py-12 px-4 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
                        <div>
                            <h3 className="text-lg font-semibold font-montserrat mb-4">{company.name}</h3>
                            <p className="text-white">CVR: {company.cvr}</p>
                        </div>
                        <div>
                            <h3 className="text-lg font-semibold mb-4">Links</h3>
                            <ul className="space-y-2">
                                {pages
                                    .filter(page => page.type !== 'homepage')
                                    .sort((a, b) => getDateValue(a.createdAt) - getDateValue(b.createdAt))
                                    .map(page => (
                                        <li key={page.id}>
                                            <Link 
                                                to={subdomain ? `/${page.slug}` : `/${cvr}/${page.slug}`}
                                                className={`${themeClasses.lightText} ${themeClasses.lightHover}`}
                                            >
                                                {page.pageName || page.title}
                                            </Link>
                                        </li>
                                    ))}
                            </ul>
                        </div>
                        {company.socialMedia && Object.keys(company.socialMedia).length > 0 && (
                            <div>
                                <h3 className="text-lg font-semibold font-montserrat mb-4">Følg os</h3>
                                <ul className="space-y-2">
                                    {company.socialMedia.facebook && (
                                        <li>
                                            <a 
                                                href={company.socialMedia.facebook}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`${themeClasses.lightText} ${themeClasses.lightHover} flex items-center`}
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M18.77 7.46H14.5v-1.9c0-.9.6-1.1 1-1.1h3V.5h-4.33C10.24.5 9.5 3.44 9.5 5.32v2.15h-3v4h3v12h5v-12h3.85l.42-4z"/>
                                                </svg>
                                                Facebook
                                            </a>
                                        </li>
                                    )}
                                    {company.socialMedia.instagram && (
                                        <li>
                                            <a 
                                                href={company.socialMedia.instagram}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`${themeClasses.lightText} ${themeClasses.lightHover} flex items-center`}
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zm0-2.163c-3.259 0-3.667.014-4.947.072-4.358.2-6.78 2.618-6.98 6.98-.059 1.281-.073 1.689-.073 4.948 0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98 1.281.058 1.689.072 4.948.072 3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98-1.281-.059-1.69-.073-4.949-.073zm0 5.838c-3.403 0-6.162 2.759-6.162 6.162s2.759 6.163 6.162 6.163 6.162-2.759 6.162-6.163c0-3.403-2.759-6.162-6.162-6.162zm0 10.162c-2.209 0-4-1.79-4-4 0-2.209 1.791-4 4-4s4 1.791 4 4c0 2.21-1.791 4-4 4zm6.406-11.845c-.796 0-1.441.645-1.441 1.44s.645 1.44 1.441 1.44c.795 0 1.439-.645 1.439-1.44s-.644-1.44-1.439-1.44z"/>
                                                </svg>
                                                Instagram
                                            </a>
                                        </li>
                                    )}
                                    {company.socialMedia.linkedin && (
                                        <li>
                                            <a 
                                                href={company.socialMedia.linkedin}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`${themeClasses.lightText} ${themeClasses.lightHover} flex items-center`}
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19 0h-14c-2.761 0-5 2.239-5 5v14c0 2.761 2.239 5 5 5h14c2.762 0 5-2.239 5-5v-14c0-2.761-2.238-5-5-5zm-11 19h-3v-11h3v11zm-1.5-12.268c-.966 0-1.75-.79-1.75-1.764s.784-1.764 1.75-1.764 1.75.79 1.75 1.764-.783 1.764-1.75 1.764zm13.5 12.268h-3v-5.604c0-3.368-4-3.113-4 0v5.604h-3v-11h3v1.765c1.396-2.586 7-2.777 7 2.476v6.759z"/>
                                                </svg>
                                                LinkedIn
                                            </a>
                                        </li>
                                    )}
                                    {company.socialMedia.tiktok && (
                                        <li>
                                            <a 
                                                href={company.socialMedia.tiktok}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                className={`${themeClasses.lightText} ${themeClasses.lightHover} flex items-center`}
                                            >
                                                <svg className="w-5 h-5 mr-2" fill="currentColor" viewBox="0 0 24 24">
                                                    <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64c.298-.002.595.042.88.13V9.4a6.33 6.33 0 0 0-1-.05A6.34 6.34 0 0 0 3.5 19.83a6.34 6.34 0 0 0 10.84-4.48V8.73a8.16 8.16 0 0 0 5.25 1.92v-3.96z"/>
                                                </svg>
                                                TikTok
                                            </a>
                                        </li>
                                    )}
                                </ul>
                            </div>
                        )}
                        <div>
                            <h3 className="text-lg font-semibold font-montserrat mb-4">Find os</h3>
                            <a 
                                href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(
                                    `${company.name} ${company.address} ${company.zipcode} ${company.city}`
                                )}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`${themeClasses.lightText} ${themeClasses.lightHover} flex items-center`}
                            >
                                <svg 
                                    className="w-5 h-5 mr-2" 
                                    fill="none" 
                                    stroke="currentColor" 
                                    viewBox="0 0 24 24"
                                >
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth="2" 
                                        d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" 
                                    />
                                    <path 
                                        strokeLinecap="round" 
                                        strokeLinejoin="round" 
                                        strokeWidth="2" 
                                        d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" 
                                    />
                                </svg>
                                Se på kort
                            </a>
                        </div>
                    </div>
                </div>
            </footer>
        </div>
    );
};

export default HomePage;